import type { SupDataGridColumn, SupStoredDataGrids } from "@/types";
import type { SortField } from "@repo/core-ui";
import { Dispatch, SetStateAction, useMemo } from "react";

import { usePersistState } from "@/hooks/usePersistState";
import { useGridColumns } from "@/store";

type SortDirection = "ASC" | "DESC";

interface filteredGridColumns {
  excludeColumns: string[];
  defaultSorting: SortField[];
  setSorting: Dispatch<SetStateAction<SortField>>;
}

export function useFilterGridColumns(
  gridName: SupStoredDataGrids,
  sortByDefault: string,
  sortByFallback: string,
  sortDirDefault: SortDirection,
  sortDirFallBack: SortDirection,
  hideColumns: string[] = [],
  controlledColumns: SupDataGridColumn[] = []
): filteredGridColumns {
  const currentGridHiddenColumns = useGridColumns(gridName, controlledColumns);

  const allHiddenColumns = useMemo(() => [...hideColumns, ...currentGridHiddenColumns.columns], [currentGridHiddenColumns.columns, hideColumns]);

  const [sorting, setSorting] = usePersistState<SortField>(`sorting-${gridName}`, {
    field: sortByDefault,
    direction: sortDirDefault,
  });

  const realSorting = useMemo(() => {
    return {
      field: sorting ? (allHiddenColumns.includes(sorting.field) ? sortByFallback : sorting.field) : sortByDefault,
      direction: sorting ? (allHiddenColumns.includes(sorting.field) ? sortDirFallBack : sorting.direction) : sortDirDefault,
    };
  }, [sorting, allHiddenColumns, sortByFallback, sortByDefault, sortDirFallBack, sortDirDefault]);

  return {
    excludeColumns: allHiddenColumns,
    defaultSorting: [realSorting],
    setSorting,
  };
}
